import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'
import {
  Briefcase,
  Camera,
  CheckCircle2,
  ChevronDown,
  FilePlus2,
  FolderOpen,
  Plus,
  Send,
  Trash2,
  UserRound,
} from 'lucide-react'
import { AppShell, PageHeader } from '../components/AppShell'
import {
  Callout,
  Field,
  Input,
  Panel,
  Select,
  Spinner,
  Toggle,
} from '../components/ui'
import { api, avatarUrl } from '../lib/api'
import { usePortal } from '../lib/portal-context'
import { cx, relativeTime } from '../lib/format'
import type { PortalUser, RoleInfo, UserCaseStats, UserRole } from '../lib/types'

const EMPTY_FORM = {
  name: '',
  email: '',
  role: 'reporter' as UserRole,
  password: '',
}

const EMPTY_STATS: UserCaseStats = {
  opened: 0,
  submitted: 0,
  handled: 0,
  views: 0,
  updates: 0,
  emails: 0,
}

export function UsersPage() {
  const { config, user: me } = usePortal()
  const [users, setUsers] = useState<PortalUser[]>([])
  const [totals, setTotals] = useState({
    accounts: 0,
    active: 0,
    opened: 0,
    handled: 0,
    submitted: 0,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all')

  const load = useCallback(async () => {
    try {
      setError(null)
      const payload = await api.listUsers()
      setUsers(payload.users)
      setTotals(payload.totals)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load users.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const create = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.createUser(form)
      setForm(EMPTY_FORM)
      setCreating(false)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the account.')
    } finally {
      setSaving(false)
    }
  }

  const roles = config?.roles ?? []
  const filtered = useMemo(() => {
    const list =
      roleFilter === 'all' ? users : users.filter((account) => account.role === roleFilter)
    return [...list].sort((a, b) => {
      const handledDiff = (b.stats?.handled ?? 0) - (a.stats?.handled ?? 0)
      if (handledDiff) return handledDiff
      return a.name.localeCompare(b.name)
    })
  }, [users, roleFilter])

  const grouped = useMemo(() => {
    const order = roles.map((role) => role.code)
    const buckets = new Map<string, PortalUser[]>()
    for (const code of order) buckets.set(code, [])
    for (const account of filtered) {
      const bucket = buckets.get(account.role) || []
      bucket.push(account)
      buckets.set(account.role, bucket)
    }
    return order
      .map((code) => ({
        role: roles.find((entry) => entry.code === code)!,
        users: buckets.get(code) || [],
      }))
      .filter((group) => group.users.length)
  }, [filtered, roles])

  return (
    <AppShell>
      <div className="mx-auto max-w-[1100px] px-4 py-7 sm:px-6">
        <PageHeader
          eyebrow="Administration"
          title="Users"
          blurb="Accounts, permission levels, and how much case work each person has handled."
          actions={
            <button
              type="button"
              className="btn-primary"
              onClick={() => setCreating((open) => !open)}
            >
              <Plus size={16} />
              {creating ? 'Cancel' : 'New account'}
            </button>
          }
        />

        {error ? (
          <Callout tone="danger" className="mb-5">
            {error}
          </Callout>
        ) : null}

        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <StatCard label="Accounts" value={totals.accounts} icon={UserRound} />
          <StatCard label="Active" value={totals.active} icon={CheckCircle2} tone="text-emerald-400" />
          <StatCard label="Cases opened" value={totals.opened} icon={FilePlus2} tone="text-sky-300" />
          <StatCard label="Cases handled" value={totals.handled} icon={Briefcase} tone="text-altair-400" />
          <StatCard
            label="Submitted"
            value={totals.submitted}
            icon={Send}
            tone="text-violet-300"
            className="col-span-2 lg:col-span-1"
          />
        </div>

        {creating ? (
          <Panel title="New account" className="mb-5">
            <form onSubmit={create} className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" required>
                <Input
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  required
                  autoFocus
                />
              </Field>
              <Field label="Email" required>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, email: event.target.value }))
                  }
                  required
                />
              </Field>
              <Field label="Permission level">
                <Select
                  value={form.role}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, role: event.target.value as UserRole }))
                  }
                >
                  {roles.map((role) => (
                    <option key={role.code} value={role.code}>
                      {role.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Temporary password" required>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, password: event.target.value }))
                  }
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                />
              </Field>
              <div className="sm:col-span-2">
                <p className="mb-3 text-[12.5px] leading-relaxed text-slate-500">
                  {roles.find((role) => role.code === form.role)?.blurb}
                </p>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? <Spinner /> : <Plus size={16} />}
                  Create account
                </button>
              </div>
            </form>
          </Panel>
        ) : null}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-1 rounded-lg border border-white/[0.07] bg-ink-900/60 p-1">
            <FilterChip active={roleFilter === 'all'} onClick={() => setRoleFilter('all')}>
              All
            </FilterChip>
            {roles.map((role) => (
              <FilterChip
                key={role.code}
                active={roleFilter === role.code}
                onClick={() => setRoleFilter(role.code)}
              >
                {role.label}
                <span className="ml-1.5 text-[10.5px] text-slate-500">
                  {users.filter((account) => account.role === role.code).length}
                </span>
              </FilterChip>
            ))}
          </div>
          <span className="ml-auto text-[12.5px] text-slate-500">
            Sorted by cases handled
          </span>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-16 text-[13px] text-slate-500">
            <Spinner /> Loading accounts…
          </div>
        ) : filtered.length === 0 ? (
          <Panel>
            <p className="py-8 text-center text-[13.5px] text-slate-500">No accounts in this filter.</p>
          </Panel>
        ) : (
          <div className="space-y-6">
            {grouped.map(({ role, users: group }) => (
              <section key={role.code}>
                <div className="mb-2 flex items-end justify-between gap-3 px-1">
                  <div>
                    <h2 className="text-[13px] font-bold uppercase tracking-[0.14em] text-slate-400">
                      {role.label}
                    </h2>
                    <p className="mt-0.5 text-[12px] text-slate-600">{role.blurb}</p>
                  </div>
                  <span className="text-[12px] text-slate-600">
                    {group.length} account{group.length === 1 ? '' : 's'}
                  </span>
                </div>

                <div className="overflow-hidden rounded-xl border border-white/[0.07] bg-ink-900/40">
                  <div className="hidden grid-cols-[minmax(0,1.4fr)_110px_88px_88px_88px_120px] gap-3 border-b border-white/[0.06] px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 md:grid">
                    <span>Person</span>
                    <span className="text-right">Handled</span>
                    <span className="text-right">Opened</span>
                    <span className="text-right">Submitted</span>
                    <span className="text-right">Views</span>
                    <span className="text-right">Last sign-in</span>
                  </div>

                  {group.map((account) => (
                    <UserRow
                      key={account.id}
                      account={account}
                      roles={roles}
                      isSelf={account.id === me?.id}
                      expanded={editingId === account.id}
                      onToggle={() =>
                        setEditingId((current) => (current === account.id ? null : account.id))
                      }
                      onChanged={load}
                      onError={setError}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone = 'text-slate-300',
  className,
}: {
  label: string
  value: number
  icon: typeof UserRound
  tone?: string
  className?: string
}) {
  return (
    <div className={cx('panel px-4 py-3.5', className)}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.13em] text-slate-500">
          {label}
        </span>
        <Icon size={15} className={tone} />
      </div>
      <div className="mt-1.5 text-[26px] font-bold leading-none tracking-[-0.02em] text-white">
        {value.toLocaleString()}
      </div>
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'rounded-md px-3 py-1.5 text-[12.5px] font-medium transition',
        active ? 'bg-white/[0.09] text-white' : 'text-slate-400 hover:text-slate-200',
      )}
    >
      {children}
    </button>
  )
}

function UserRow({
  account,
  roles,
  isSelf,
  expanded,
  onToggle,
  onChanged,
  onError,
}: {
  account: PortalUser
  roles: RoleInfo[]
  isSelf: boolean
  expanded: boolean
  onToggle: () => void
  onChanged: () => Promise<void>
  onError: (message: string | null) => void
}) {
  const [name, setName] = useState(account.name)
  const [email, setEmail] = useState(account.email)
  const [role, setRole] = useState<UserRole>(account.role)
  const [password, setPassword] = useState('')
  const [active, setActive] = useState(account.active)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    setName(account.name)
    setEmail(account.email)
    setRole(account.role)
    setActive(account.active)
    setPassword('')
    setConfirmDelete(false)
  }, [account])

  const stats = account.stats ?? EMPTY_STATS
  const photo = avatarUrl(account.avatarFile)

  const save = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    onError(null)
    try {
      await api.updateUser(account.id, {
        name,
        email,
        role,
        active,
        ...(password ? { password } : {}),
      })
      setPassword('')
      await onChanged()
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Could not update the account.')
    } finally {
      setSaving(false)
    }
  }

  const onAvatar = async (file: File | null) => {
    if (!file) return
    setSaving(true)
    onError(null)
    try {
      await api.uploadAvatar(account.id, file)
      await onChanged()
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Could not upload the photo.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    setSaving(true)
    onError(null)
    try {
      await api.deleteUser(account.id)
      await onChanged()
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Could not delete the account.')
      setSaving(false)
    }
  }

  return (
    <div className="border-b border-white/[0.05] last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="grid w-full grid-cols-1 items-center gap-3 px-4 py-3.5 text-left transition hover:bg-white/[0.025] md:grid-cols-[minmax(0,1.4fr)_110px_88px_88px_88px_120px]"
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/[0.06] text-slate-400">
            {photo ? (
              <img src={photo} alt="" className="h-full w-full object-cover" />
            ) : (
              <UserRound size={18} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-[14.5px] font-semibold text-white">{account.name}</span>
              {isSelf ? (
                <span className="rounded bg-altair-500/20 px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-altair-300">
                  You
                </span>
              ) : null}
              {!account.active ? (
                <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
                  Inactive
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 truncate text-[12.5px] text-slate-500">{account.email}</div>
            <div className="mt-1.5 flex flex-wrap gap-2 text-[11.5px] text-slate-500 md:hidden">
              <span>{stats.handled} handled</span>
              <span>·</span>
              <span>{stats.opened} opened</span>
              <span>·</span>
              <span>{stats.submitted} submitted</span>
            </div>
          </div>
          <ChevronDown
            size={16}
            className={cx(
              'shrink-0 text-slate-600 transition md:hidden',
              expanded && 'rotate-180',
            )}
          />
        </div>

        <Metric value={stats.handled} emphasize />
        <Metric value={stats.opened} />
        <Metric value={stats.submitted} />
        <Metric value={stats.views} />
        <div className="hidden text-right text-[12px] text-slate-500 md:block">
          {account.lastLoginAt ? relativeTime(account.lastLoginAt) : 'Never'}
        </div>
      </button>

      {expanded ? (
        <form onSubmit={save} className="border-t border-white/[0.06] bg-black/20 px-4 py-4">
          <div className="mb-4 grid gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-3 sm:grid-cols-4">
            <MiniStat icon={Briefcase} label="Handled" value={stats.handled} />
            <MiniStat icon={FolderOpen} label="Opened" value={stats.opened} />
            <MiniStat icon={Send} label="Submitted" value={stats.submitted} />
            <MiniStat icon={FilePlus2} label="Updates / emails" value={`${stats.updates} / ${stats.emails}`} />
          </div>

          <div className="mb-4 flex items-center gap-3">
            <label className="relative flex h-16 w-16 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-white/[0.06] text-slate-400 transition hover:bg-white/[0.1]">
              {photo ? (
                <img src={photo} alt="" className="h-full w-full object-cover" />
              ) : (
                <Camera size={18} />
              )}
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(event) => void onAvatar(event.target.files?.[0] ?? null)}
              />
            </label>
            <div className="text-[12.5px] leading-relaxed text-slate-500">
              Optional profile photo. Click the circle to upload (max 5 MB).
              {account.avatarFile ? (
                <button
                  type="button"
                  className="mt-1 block text-slate-400 underline-offset-2 hover:text-white hover:underline"
                  onClick={() =>
                    void api
                      .removeAvatar(account.id)
                      .then(onChanged)
                      .catch((cause) =>
                        onError(cause instanceof Error ? cause.message : 'Could not remove photo.'),
                      )
                  }
                >
                  Remove photo
                </button>
              ) : null}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name">
              <Input value={name} onChange={(event) => setName(event.target.value)} required />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </Field>
            <Field label="Permission level">
              <Select
                value={role}
                onChange={(event) => setRole(event.target.value as UserRole)}
                disabled={isSelf}
              >
                {roles.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="New password">
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={8}
                placeholder="Leave blank to keep current"
              />
            </Field>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <Toggle
              checked={active}
              onChange={setActive}
              disabled={isSelf}
              label={active ? 'Account active' : 'Account inactive'}
            />
            <div className="flex flex-wrap gap-2">
              {!isSelf ? (
                confirmDelete ? (
                  <>
                    <button
                      type="button"
                      className="btn-danger"
                      disabled={saving}
                      onClick={() => void remove()}
                    >
                      {saving ? <Spinner /> : <Trash2 size={14} />}
                      Confirm delete
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => setConfirmDelete(false)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn-subtle text-slate-500 hover:text-red-300"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                )
              ) : null}
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? <Spinner /> : null}
                Save changes
              </button>
            </div>
          </div>
        </form>
      ) : null}
    </div>
  )
}

function Metric({ value, emphasize }: { value: number; emphasize?: boolean }) {
  return (
    <div
      className={cx(
        'hidden text-right font-mono text-[13px] md:block',
        emphasize ? 'font-semibold text-white' : 'text-slate-400',
      )}
    >
      {value.toLocaleString()}
    </div>
  )
}

function MiniStat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Briefcase
  label: string
  value: number | string
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon size={14} className="shrink-0 text-slate-600" />
      <div>
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-600">
          {label}
        </div>
        <div className="text-[14px] font-semibold text-white">{value}</div>
      </div>
    </div>
  )
}
